<template>
  <div
    @dragover.prevent="onDragOver"
    @drop.prevent="onDrop">
    <div class="app-container top-padding" uk-height-viewport="expand: true">
        <div v-if="dragOver">
          <div :class="{'default-background': !maxFilesExceeded, 'error-background': maxFilesExceeded}"
               class="file-drag">
            <div class="uk-position-center">
              <div class="uk-flex uk-flex-center">
                <img :src="largeWhiteShareIcon" v-if="!maxFilesExceeded">
                <img :src="largeWhiteWarningIcon" v-else>
              </div>
              <div class="file-drop">
                <span v-if="!maxFilesExceeded">{{$t('shareResource.dropFile')}}</span>
                <span v-else>{{$t('shareResource.oneFile')}}</span>
              </div>
            </div>
          </div>
        </div>
      <div uk-grid>
        <div class="uk-width-1-2">
          <div
            class="drop-zone-column"
            uk-sticky :offset="stickyOffset">
            <span class="heading-style">{{$t('general.upload')}}</span>
            <p>
              <span class="link" v-html="$t('shareResource.guide')"></span>
            </p>
            <drop-zone
              v-show="showDropZone"
              @resource-selected="resolveFile">
            </drop-zone>
            <uploader
              v-show="showUploader"
              :file="file"
              :resourceId="contentId"
              @cancel="setDefaults"
              @contentId="resolveContentId"
              @uploadError="resolveError"
              @processingStatus="resolveProcessing">
            </uploader>
            <uploader-error
              v-if="error"
              :error="error"
              @selectFile="setDefaults"
              @cancel="setDefaults"
              @retry="retryUpload">
            </uploader-error>
            <resource-preview
              v-if="previewList"
              :previewList="previewList"
              @changeFile="changeFile">
            </resource-preview>
          </div>
        </div>
        <div class="uk-width-1-2 metadata-column">
          <upload-details-input
            :fileName="fileName"
            :contentId="contentId"
            @metadata-input-count="resourceValidation"
            :metadata="resourceMetaData"
            @upload-complete="destroyBeforeUnloadListener"
            :shareDisabled="shareDisabled"
            :userLocation="userLocation">
          </upload-details-input>
        </div>
      </div>
      <share-resource-f-a-q></share-resource-f-a-q>
      <file-drop-error></file-drop-error>
    </div>
    </div>
</template>

<script>
  import UIkit from 'uikit'
  import dropZone from './upload/drop-zone'
  import uploader from './upload/uploader'
  import uploaderError from './errors/uploader-error'
  import uploadDetailsInput from './upload/upload-details-input'
  import FileDropError from '../../ui-components/modals/file-drop-error'
  import ShareResourceFAQ from '../../ui-components/modals/share-resource-faq'
  import iconUtil from '../../../utils/icon-util'
  import * as misc from '../../../enums/misc'
  import * as resource from '../../../enums/resource-sharing'
  import * as url from '../../../enums/static-urls'
  import fileUtil from '../../../utils/file-util'
  import * as errorCode from '../../../enums/error-codes'
  import {mapActions, mapGetters, mapMutations} from 'vuex'
  import * as _ from 'lodash'
  import screenUtil from '../../../utils/screen-util'
  import resourcePreview from './upload/resource-preview'
  import metrics from '../../../services/metrics'
  import * as idStore from '../../../enums/id-store-keys'
  import * as countryCode from '../../../enums/supported-countries'
  import * as languageCode from '../../../enums/supported-languages'

  let timeout = null
  export default {
    name: 'share-resource',
    components: {
      resourcePreview,
      dropZone,
      ShareResourceFAQ,
      FileDropError,
      uploader,
      uploaderError,
      uploadDetailsInput
    },
    data() {
      return {
        content: null,
        contentId: null,
        dragOver: false,
        error: null,
        file: null,
        previewList: null,
        retry: false,
        largeWhiteShareIcon: iconUtil.resolve(misc.SHARE_ICON_WHITE_LARGE),
        largeWhiteWarningIcon: iconUtil.resolve(misc.WARNING_ICON_WHITE_LARGE),
        maxFilesExceeded: false,
        resourceGuide: url.RESOURCE_CREATION_GUIDE,
        stickyOffset: 85,
        resourceMetaData: null,
        shareDisabled: true,
        userLocation: null
      }
    },
    metaInfo() {
      return {
        meta: [
          { name: 'robots',
            content: 'noindex'
          }
        ]
      }
    },
    computed: {
      ...mapGetters('auth/id', ['authenticated']),
      ...mapGetters(['getResourceDataAdded', 'accessToken']),
      fileName() {
        if (this.file !== null) {
          return fileUtil.fileName(this.file) || ''
        } else {
          return ''
        }
      },
      showDropZone() {
        return !this.file && !this.error && !this.retry && !this.previewList
      },
      showUploader() {
        return this.file && !this.error && !this.previewList
      }
    },
    methods: {
      ...mapActions(['fetchIDData', 'fetchContentDetail', 'deleteResource', 'resolveUserLocation', 'cleanSetSearchCriteria']),
      ...mapMutations(['setResourceDataAdded', 'setCountries', 'setLanguages']),
      onDragOver(evt) {
        // only register the drag over if there is no file already uploading
        if (!this.file) {
          this.resolveMaxFilesExceeded(evt)
          this.resolveDragOver()
        }
      },
      resolveDragOver() {
        if (timeout) {
          clearTimeout(timeout)
        }
        this.dragOver = true
        timeout = setTimeout(() => {
          this.dragOver = false
        }, 100)
      },
      resolveMaxFilesExceeded(evt) {
        let items = evt.dataTransfer.items
        let files = (items && items.length > 0) ? evt.dataTransfer.items : evt.dataTransfer.files
        this.maxFilesExceeded = files.length > resource.MAX_FILES
      },
      onDrop(evt) {
        // only register the drop if there is no file already uploading
        if (!this.file) {
          if (this.error) {
            this.error = null
          }
          if (evt.dataTransfer.files.length > resource.MAX_FILES) {
            UIkit.modal('#file-drop-error').show()
          } else {
            if (fileUtil.acceptedType(evt.dataTransfer.files[0])) {
              this.file = evt.dataTransfer.files[0]
            } else {
              this.error = {
                heading: this.$t('error.uploadError.defaultMessage') + ' ' + fileUtil.fileName(evt.dataTransfer.files[0]),
                message: this.$t('error.uploadError.CP401.message'),
                suggestion: this.$t('error.uploadError.CP401.suggestion'),
                code: errorCode.CP_401
              }
            }
          }
        }
      },
      redirect(url) {
        window.open(url, '_blank')
      },
      changeFile() {
        if (!this.resourceMetaData) {
          this.deleteResource(this.contentId)
            .then(() => {
              this.setDefaults()
            })
        } else {
          this.error = null
          this.file = null
          this.content = null
          this.previewList = null
        }
      },
      setDefaults() {
        this.error = null
        this.file = null
        this.content = null
        this.previewList = null
        this.contentId = null
        this.shareDisabled = true
      },
      resolveContentId(id) {
        this.contentId = id
      },
      resolveFile(file) {
        this.file = file
      },
      resolveError(error) {
        this.error = error
      },
      displayFAQModal() {
        UIkit.modal('#share-resource-faq').show()
      },
      fetchShareCompleted() {
        let keyFound = false
        this.fetchIDData()
          .then(result => {
            if (!_.isEmpty(result.body)) {
              for (let key in result.body) {
                if (key === idStore.SHARE_COMPLETE) {
                  keyFound = true
                  if (result.body[key] !== 'true') {
                    this.displayFAQModal()
                  }
                }
              }
              if (!keyFound) {
                this.displayFAQModal()
              }
            } else {
              this.displayFAQModal()
            }
          }).catch(err => {
            metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.FETCH_ID_STORE_ERROR, 1, metrics.TYPE.FAILURE_COUNTER, err)
          })
      },
      resolveWindowWidth() {
        if (screenUtil.resolveWidth() > 1024) {
          this.stickyOffset = 85
        } else {
          this.stickyOffset = 105
        }
      },
      retryUpload() {
        let retryFile = this.file
        this.retry = true
        this.error = null
        this.file = null
        this.shareDisabled = true
        // timeout required to trigger file watcher in uploader
        setTimeout(() => {
          this.retry = false
          this.file = retryFile
        }, 100)
      },
      resolveProcessing(status) {
        if (status === 'processed') {
          this.fetchContentDetail({accessToken: this.accessToken, id: this.contentId, region: null})
            .then(() => {
              // set the preview list if we have a resource
              if (this.contentId) {
                this.content = this.getDetails(this.contentId)
                this.previewList = this.content.detail.previewerList
              }
              this.shareDisabled = false
            })
            .catch(err => {
              metrics.sendMetric(metrics.CATEGORIES.THIRD_PARTY_CALLS, metrics.CP_TAGS.FETCH_CONTENT_DETAIL_ERROR, 1, metrics.TYPE.FAILURE_COUNTER, err)
            })
        }
      },
      resolveSupportedCountries() {
        let supportedCountries = this.$t('countries')
        let supportedCountriesArr = []
        for (let country in supportedCountries) {
          let countryObj = {}
          countryObj.id = this.resolveCountryCode(country)
          countryObj.title = supportedCountries[country]
          countryObj.regionName = this.resolveCountryRegion(country)
          supportedCountriesArr.push(countryObj)
        }
        this.setCountries(supportedCountriesArr)
      },
      resolveCountryCode(country) {
        return countryCode.SUPPORTED_COUNTRIES.find(o => o.title === country).ID
      },
      resolveCountryRegion(country) {
        return countryCode.SUPPORTED_COUNTRIES.find(o => o.title === country).regionName
      },
      resolveSupportedLanguages() {
        let supportedLanguages = this.$t('languages')
        let supportedLanguagesArr = []
        for (let language in supportedLanguages) {
          let languageObj = {}
          languageObj.id = this.resolveLanguageCode(language)
          languageObj.title = supportedLanguages[language]
          supportedLanguagesArr.push(languageObj)
        }
        this.setLanguages(supportedLanguagesArr)
      },
      resolveLanguageCode(language) {
        return languageCode.SUPPORTED_LANGUAGES.find(o => o.title === language).ID
      },
      getDetails(id) {
        // mapGetters does not work for functions like this nicely
        return this.$store.getters.getDetails(id)
      },
      createBeforeUnloadListener() {
        window.addEventListener('beforeunload', this.promptConfig)
      },
      destroyBeforeUnloadListener() {
        window.removeEventListener('beforeunload', this.promptConfig)
      },
      resourceValidation(keyCount) {
        if (this.file || keyCount) {
          if (!this.getResourceDataAdded) {
            this.createBeforeUnloadListener()
            this.setResourceDataAdded(true)
          }
        } else if (!this.file && !keyCount) {
          this.destroyBeforeUnloadListener()
          this.setResourceDataAdded(false)
        }
      },
      promptConfig(event) {
        let message = 'If you leave before sharing your changes will be lost.';
        (event || window.event).returnValue = message
        return message
      },
      resolveMetadata() {
        if (this.$route.params.metadata) {
          this.resourceMetaData = this.$route.params.metadata
          if (this.resourceMetaData.id) {
            this.shareDisabled = false
            this.contentId = this.resourceMetaData.id
            this.fetchContentDetail({id: this.contentId, region: null})
              .then(() => {
                this.content = this.getDetails(this.contentId)
                this.previewList = this.content.detail.previewerList
              })
          }
        }
      }
    },
    created() {
      if (!this.authenticated) {
        let state = {
          from: this.$route.path,
          download: false
        }
        this.$auth.id.signIn(state)
      }
      this.cleanSetSearchCriteria(null)
      this.resolveMetadata()
      this.resolveUserLocation()
        .then(result => {
          this.userLocation = result.body
          // not ideal but required to allow the translated countries list to be set in the vuex store
        })
      this.resolveSupportedCountries()
      this.resolveSupportedLanguages()
    },
    mounted() {
      window
        .addEventListener(
          'resize',
          this.resolveWindowWidth)
      this.resolveWindowWidth()
      this.fetchShareCompleted()
    },
    destroyed() {
      window
        .removeEventListener(
          'resize',
          this.resolveWindowWidth)
    },
    watch: {
      file(value) {
        this.resourceValidation()
      },
      getResourceDataAdded(value) {
        if (!value) {
          this.destroyBeforeUnloadListener()
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../../../styles/breakpoints';
  @import '../../../styles/colours';

  a {
    color: $content-link-blue;
    font-weight: bold;
  }

  .default-background {
    background-color: $file-drag-background-color;
  }

  .drop-zone-column {
    padding-left: 100px
  }

  .error-background {
    background-color: $file-drag-error-background-color;
  }

  .file-drag {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1000;
  }

  .file-drop {
    font-size: 36px;
    font-weight: bold;
    color: white;
    margin-top: 60px;
  }

  .guide-link-style {
    font-size: 16px;
    color: $content-link-blue;
    font-weight: bold;
    &:hover {
      text-decoration: underline;
    }
  }

  .heading-style {
    font-size: 30px;
    font-weight: bold;
    color: $content-text;
  }

  .top-padding {
    padding-top: 86px;
  }

  .link {
    /deep/
    a {
      color: $content-link-blue;
      font-weight: bold;
      &:hover {
        text-decoration: underline;
      }
    }
  }

  .metadata-column {
    padding-left: 24px
  }

  @media screen and (max-width: $breakpoint-medium) {

    .app-container {
      width: 100%;
    }

    .top-padding {
      padding-top: 115px;
    }

  }
</style>
